import React, { useEffect, useRef, useState, useCallback, forwardRef, useMemo } from 'react';
import { useForm, FormProvider, useWatch, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ZodObject, ZodTypeAny, z as zod } from 'zod';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Typography,
  Stack,
  MenuItem,
  Divider,
  InputAdornment,
  IconButton,
  Tooltip as MuiTooltip,
  TooltipProps as MuiTooltipProps,
  Button,
  Card,
  Alert,
  Checkbox,
  AlertTitle,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  DialogContentText,
  useMediaQuery,
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router';
import moment from 'moment-timezone';
import { Markdown } from '../markdown';
import RequirementUploader from './requirements-uploader';
import DndPropertiesSelector from './properties_dnd';
import { ImageUploadPreview } from './ImageUploadPreview';
import { RequirementsManagementView } from '../../sections/parkcity-components/admin/control-panel/requirements-management-view';
import { Field, Form, RHFCode, schemaHelper } from 'src/components/hook-form';
import { Scrollbar } from 'src/components/scrollbar';
import { Iconify } from 'src/components/iconify';
import { toast } from 'src/components/snackbar';
import debounce from 'lodash.debounce';
import { useBoolean } from 'src/hooks/use-boolean';
import { Icon } from '@iconify/react';
import { useTranslate } from 'src/locales';
import { humanizeLabelKey } from 'src/utils/change-case';
import { maxLine } from 'src/theme/styles';
import FormConstants from 'src/constants/FormConstants';
import AuthUtils from 'src/utils/AuthUtils';
import { decodeJWT } from 'aws-amplify/auth';
import EncryptUtils from 'src/utils/EncryptUtils';
import {
  executeAdminActions,
  executeUserActions,
  getBannerDetails,
  revertTabToDefaults,
} from 'src/services/service-handlers/PrivateService';
import { useAuthContext } from 'src/context/AuthContext';
import {
  checkUserName,
  sendOtp,
  signUpService,
  verifyOtp,
} from 'src/services/service-handlers/PublicService';
import { injectTemplateFields } from 'src/utils/templateUtils';
import { useBannerDetails } from 'src/context/BannerContext';
import UploadArea from './UploadArea';
import { formatDateTime, useHasPermission } from 'src/utils/helper';
import { useModulePermissions } from 'src/hooks/use-module-permissions';
import { DatePicker, DateTimePicker } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';

// import { FormData } from './form-loader';

interface TooltipProps extends Omit<MuiTooltipProps, 'children'> {
  children: React.ReactElement;
  title: string;
  infoButton?: boolean;
}

const Tooltip = forwardRef<HTMLDivElement, TooltipProps>(({ children, ...props }, ref) => (
  <MuiTooltip
    ref={ref}
    {...props}
    componentsProps={{
      tooltip: {
        sx: {
          fontSize: '16px',
        },
      },
    }}
  >
    <Box
      component="span"
      display={props?.infoButton ? 'inline' : 'inline-block'}
      width={props?.infoButton ? 'min-content' : '100%'}
    >
      {children}
    </Box>
  </MuiTooltip>
));
Tooltip.displayName = 'Tooltip';

interface RadioOptions {
  label: string;
  value: any;
  countryCode: string;
}
interface Column {
  name: string;
  label: string;
  type: string;
  options?: RadioOptions[];
  validation_schema?: string;
}

interface UploadsRowData {
  label: string;
  description_text: string;
  file_parameters: {
    accept_file_size_bytes: number;
    accept_file_types: string[];
    required_level: string;
    prevent_submit_if_empty: boolean;
  };
}
interface SectionData {
  order: number;
  section_label: string;
  hash?: string;
  render_vertical?: boolean;
  is_upload_section?: boolean;
  ui_rows_sql_data?: any;
  fields: any;
  rows: UploadsRowData[];
}

interface FormData {
  form_type: string;
  name?: string;
  sections: SectionData[];
  translations: Record<string, Record<string, string>>;

  // From form-builder.tsx
  showAuditInfo?: boolean;
  show_cancel_button?: boolean;
  submit_button_text: any; // kept 'any' since builder uses any
  rescind_button_text?: string;
  emailTemplate?: any;
  emailTemplates?: any;
  submit_button_sql?: any;
  notification_sql_data?: any;
  is_notification_enabled?: boolean;
  submit_button_insert_sql: any;
  submit_button_update_sql: any;
  rescind_button_sql?: any;
  requires_confirmation?: boolean;
  confirmation?: any;
  terms_acknowledgement?: boolean;
  terms_acknowledgement_text?: string;
  terms_acknowledgement_text_es?: string;
  terms_acknowledgement_heading?: string;
  terms_acknowledgement_body?: string;
  terms_acknowledgement_body_es?: string;
  disabled_by_default?: boolean;
  encrypted_key_on_submit?: string;
  get_raw_data_sql?: string;

  // Flags
  conditional_disable_form?: boolean;
  disable_condition?: {
    // Support both notations
    fieldName?: string;
    fieldValue?: any;
    conditionField?: string;
    conditionValue?: any;
  };
  disable_isDirty?: boolean;
  edit_button_text?: string;
  edit_button_sql?: any;
  helper_message?: any;

  // From form-loader.tsx
  sql_data?: any;
  form_actions?: any;
  enable_otp_verification?: boolean;
  signupType?: any;
}

interface UserSignUpFormProps {
  defaultValues: Record<string, string>;
  schema: ZodObject<Record<string, ZodTypeAny>>;
  formDataParsed: FormData;
  stageMessage?: string;
  isFullApplicationEnabled?: boolean;
  fullApplicationIncomeCategory?: number;
  module_name?: string;
  sub_module?: string;
  defaultData?: any;
  isAdmin: boolean;
  refreshForm?: () => void;
  onRefreshFullApplicationStatus?: () => void;
  scrollNeeded: boolean;
  signupType?: any;
  /** When control-panel > landing-page-content: include in submit for language-specific save */
  landingPageLang?: 'en' | 'es';
  /** When control-panel > system-settings: include in submit for Help URL/label language-specific save */
  systemSettingsLang?: 'en' | 'es';
}

interface UserPayload {
  PersonId: string;
  UserId: string;
  RecordId: string;
}

type CustomInputProps = {
  field: any;
  fieldSx: any;
  inputSx: any;
  key: any;
  sectionDisabled?: boolean;
  sectionLabel?: string;
};

const validationMapping = {
  name: schemaHelper.name,
  email: schemaHelper.email,
  phoneNumber: schemaHelper.phoneNumber,
  referral: schemaHelper.referral,
  text: schemaHelper.text,
  password: schemaHelper.password,
  date: schemaHelper.date,
  editor: schemaHelper.editor,
  objectOrNull: schemaHelper.objectOrNull,
  boolean: schemaHelper.boolean,
  file: schemaHelper.file,
  files: schemaHelper.files,
  number: schemaHelper.number,
  floatNumber: schemaHelper.floatNumber,
  array: schemaHelper.array,
};

// --- helpers --------------------------------------------------------------

/** Robust comparer that supports array values and number/string coercion */
const valuesInclude = (needle: unknown, haystack: unknown[]): boolean => {
  const n = needle == null ? '' : String(needle);
  return haystack.some((h) => String(h) === n);
};

const isConditionMet = (
  condition: { fieldName: string; fieldValue: any },
  values: Record<string, any>
) => {
  if (!condition?.fieldName) return false;
  const actual = values?.[condition.fieldName];
  const expected = condition.fieldValue;

  if (Array.isArray(expected)) return valuesInclude(actual, expected);
  return String(actual) === String(expected);
};

// Build filtered schema based on conditional renders in fields
const getFilteredSchema = (
  values: Record<string, any>,
  schema: ZodObject<Record<string, ZodTypeAny>> | null,
  formDataParsed: FormData
) => {
  if (!schema?.shape) return zod.object({});
  const updated: Record<string, ZodTypeAny> = {};
  formDataParsed.sections?.forEach((section) => {
    section.fields?.forEach((field: any) => {
      const { name, required, conditionalRender, condition } = field;
      if (
        conditionalRender &&
        condition &&
        !isConditionMet(
          {
            fieldName: condition.fieldName ?? condition.conditionField,
            fieldValue: condition.fieldValue ?? condition.conditionValue,
          },
          values
        )
      ) {
        return;
      }
      const v = (schema as any).shape?.[name];
      if (v) updated[name] = required ? v : v.optional();
    });
  });
  return zod.object(updated);
};

// -------------------------------------------------------------------------

export function FormBuilder({
  defaultValues,
  schema,
  formDataParsed,
  stageMessage,
  isFullApplicationEnabled,
  fullApplicationIncomeCategory,
  module_name,
  sub_module,
  defaultData,
  isAdmin,
  refreshForm = () => {},
  onRefreshFullApplicationStatus,
  scrollNeeded = true,
  signupType,
  landingPageLang,
  systemSettingsLang,
}: UserSignUpFormProps) {
  // console.log(formDataParsed, 'formDataParsed');
  const navigate = useNavigate();
  const initialSchema = getFilteredSchema(defaultValues, schema, formDataParsed);
  const { authState } = useAuthContext();
  const { isAuthenticated } = authState;
  const { bannerDetails, setBannerDetails } = useBannerDetails();
  const paramsData: Record<string, any> = EncryptUtils.getDecryptedData(
    window.location.search,
    'row'
  );
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const verifiedEmailRef = useRef<string | null>(null);

  const [filteredFormSchema, setFilteredFormSchema] =
    useState<ZodObject<Record<string, ZodTypeAny>>>(initialSchema);
  const [acknowledged, setAcknowledged] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');

  const [isEmailValid, setIsEmailValid] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [sendOtpLoading, setSendOtpLoading] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);

  const [checkingUsername, setCheckingUsername] = useState(false);
  const [isUsernameAvailable, setIsUsernameAvailable] = useState<boolean | null>(null);

  const [isCheckingForProperties, setIsCheckingForProperties] = useState(false);
  const [clearPropertiesOnRescind, setClearPropertiesOnRescind] = useState(0);
  const [isImageUploading, setIsImageUploading] = useState(false);
  const imageUploadFunctionsRef = useRef<Map<string, () => Promise<string | null>>>(new Map());
  const [revertDialogOpen, setRevertDialogOpen] = useState(false);
  const [reverting, setReverting] = useState(false);
  const [requirementsValid, setRequirementsValid] = useState(true);

  // Dynamically size the scroll area to fill from its real top position down to the
  // viewport bottom. This adapts to any page header height (e.g. tall admin headers vs
  // short user-page headers) so the sticky footer is always flush at the bottom — never
  // clipped on short screens and never floating with a gap on tall ones.
  const scrollWrapperRef = useRef<HTMLDivElement | null>(null);
  const [scrollMaxHeight, setScrollMaxHeight] = useState<string>('calc(100vh - 260px)');

  useEffect(() => {
    if (!scrollNeeded) return undefined;
    const recompute = () => {
      const el = scrollWrapperRef.current;
      if (!el || typeof window === 'undefined') return;
      const top = el.getBoundingClientRect().top;
      const BOTTOM_GAP = 16; // small breathing room above the viewport edge
      const available = Math.max(240, window.innerHeight - top - BOTTOM_GAP);
      setScrollMaxHeight(`${Math.round(available)}px`);
    };
    recompute();
    window.addEventListener('resize', recompute);
    return () => window.removeEventListener('resize', recompute);
  }, [scrollNeeded, formDataParsed, defaultData, stageMessage]);

  // ──── Track initial values for General/Members/Requirements sections only ────
  const initialOtherSectionsValuesRef = useRef<Record<string, any>>({});
  const initialValuesCapturedRef = useRef<boolean>(false);

  // ───────── permissions management ──────────────────────────────────────────────
  const hasPermission = useHasPermission();
  const modulePermissions = useModulePermissions(module_name);
  
  // Check if user can edit Control Panel (for Update and Revert buttons)
  // Only apply permission checks for admin users
  const canEditControlPanel = isAdmin && module_name === 'control-panel' 
    ? modulePermissions.canEdit
    : true;
  
  // Check if user can edit Entities/Properties/Person Details detail pages
  // Only apply permission checks for admin users
  const canEditDetails = isAdmin ? modulePermissions.canEditDetails : true;

  // Helper to extract field names from specific sections only
  const getTargetSectionFieldNames = useCallback(() => {
    if (!formDataParsed?.sections) return [];

    const targetSectionLabels = ['generalInformation', 'membersSection', 'requirements'];
    const fieldNames: string[] = [];

    formDataParsed.sections.forEach((section: any) => {
      if (targetSectionLabels.includes(section.section_label)) {
        section.fields?.forEach((field: any) => {
          if (field.name) {
            fieldNames.push(field.name);
          }
        });
      }
    });

    return fieldNames;
  }, [formDataParsed]);

  // Deep comparison helper for values
  const deepEqual = useCallback((obj1: any, obj2: any): boolean => {
    if (obj1 === obj2) return true;
    if (obj1 == null || obj2 == null) return obj1 === obj2;
    if (typeof obj1 !== typeof obj2) return false;

    if (Array.isArray(obj1) && Array.isArray(obj2)) {
      if (obj1.length !== obj2.length) return false;
      return obj1.every((item, index) => deepEqual(item, obj2[index]));
    }

    if (typeof obj1 === 'object') {
      const keys1 = Object.keys(obj1);
      const keys2 = Object.keys(obj2);
      if (keys1.length !== keys2.length) return false;
      return keys1.every((key) => deepEqual(obj1[key], obj2[key]));
    }

    // For primitives, use string comparison to handle number/string coercion
    return String(obj1) === String(obj2);
  }, []);

  // determine section should be disabled - ONLY affects "General Information" and "Requirements" sections in case of Full application details
  const shouldDisableSection = useCallback(
    (section: any) => {
      const isFullApplicationForm =
        formDataParsed?.name === 'full-application' ||
        formDataParsed?.name === 'full-application-details';
      if (!isFullApplicationForm) return false; // Guard 1: Only apply to Full Application forms (both user and admin)
      if (formDataParsed?.name === 'full-application' && !isFullApplicationEnabled) return false; // Guard 1b: For user side, also check isFullApplicationEnabled prop

      const hasPropertiesSection = formDataParsed?.sections?.some(
        (s: any) =>
          s.section_label === 'selectProperties' &&
          s.fields?.some((field: any) => field.type === 'dnd')
      );
      if (!hasPropertiesSection) return false; // Guard 2: Only apply when selectProperties section with dnd field exists

      const sectionLabel = section.section_label;
      const isTargetSection =
        sectionLabel === 'generalInformation' ||
        sectionLabel === 'requirements' ||
        sectionLabel === 'membersSection';
      if (!isTargetSection) return false; // Guard 3: Only target generalInformation, requirements, and membersSection sections

      return !isCheckingForProperties; // Guard 4: Disable only when properties are NOT saved to database
    },
    [isFullApplicationEnabled, formDataParsed, isCheckingForProperties]
  );

  const [confirmationDialog, setConfirmationDialog] = useState<{
    open: boolean;
    title?: string;
    message: string;
    icon?: string;
    confirmText?: string;
    cancelText?: string;
    onConfirm: () => Promise<void> | void;
  }>({
    open: false,
    message: '',
    onConfirm: () => {},
  });

  const isUpdateMode = Object.keys(defaultData || {}).length > 0;
  const password = useBoolean();
  const dialog = useBoolean();
  const theme = useTheme();
  const { t, currentLang } = useTranslate('form');
  const { t: tCommon } = useTranslate('common');

  const isEs = currentLang?.value === 'es';
  const termsBody = isEs
    ? (formDataParsed?.terms_acknowledgement_body_es ?? formDataParsed?.terms_acknowledgement_body)
    : formDataParsed?.terms_acknowledgement_body;
  const formattedMarkdown = termsBody?.replaceAll('\\n', '\n');

  const token = isAuthenticated ? AuthUtils.getAuthToken() : null;
  const decodedToken = isAuthenticated && token ? decodeJWT(token) : null;
  const tokenData = decodedToken as { payload?: { user?: UserPayload } } | null;

  const isDisabledByDefault = formDataParsed?.disabled_by_default === true;
  const fullAppEnabled = isFullApplicationEnabled === true;
  const isFormDisabled = isDisabledByDefault && !fullAppEnabled;

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Ref-based resolver: validation always uses latest schema (e.g. after language change) without re-initializing useForm or losing form state.
  const schemaRef = useRef(filteredFormSchema);
  schemaRef.current = filteredFormSchema;
  const methods = useForm({
    resolver: (values, context, options) =>
      zodResolver(schemaRef.current)(values, context, options),
    defaultValues,
    mode: 'onBlur',
  });

  const {
    handleSubmit,
    formState: { isSubmitting, errors, isDirty },
    trigger,
    watch,
    setError,
    clearErrors,
    setValue,
  } = methods;

  // live values from the form (only for fields inside the form)
  const watchedValues = useWatch({ control: methods.control });

  // Capture initial values for target sections when form loads or defaultValues change
  useEffect(() => {
    // Only capture initial values when defaultValues is available (not undefined/null)
    // This prevents false positive "dirty" state during initial load when API is still fetching
    if (!defaultValues || !formDataParsed) {
      initialValuesCapturedRef.current = false;
      return;
    }

    const targetFieldNames = getTargetSectionFieldNames();

    // If no target fields exist, mark as captured but skip
    if (targetFieldNames.length === 0) {
      initialValuesCapturedRef.current = true;
      return;
    }

    const initialValues: Record<string, any> = {};

    targetFieldNames.forEach((fieldName) => {
      // Deep clone to avoid reference issues
      const value = defaultValues[fieldName];
      // Store the value even if it's undefined/null/empty to track the initial state
      if (typeof value === 'object' && value !== null) {
        try {
          initialValues[fieldName] = JSON.parse(JSON.stringify(value));
        } catch {
          initialValues[fieldName] = value;
        }
      } else {
        initialValues[fieldName] = value;
      }
    });

    initialOtherSectionsValuesRef.current = initialValues;
    initialValuesCapturedRef.current = true;
  }, [defaultValues, formDataParsed, getTargetSectionFieldNames]);

  // Check if other sections (General/Members/Requirements) have unsaved changes
  const isOtherSectionsDirty = useMemo(() => {
    // Don't mark as dirty until initial values are captured (prevents false positive during initial load)
    if (!initialValuesCapturedRef.current) return false;

    if (!watchedValues || !formDataParsed) return false;

    const targetFieldNames = getTargetSectionFieldNames();

    // If no target fields, not dirty
    if (targetFieldNames.length === 0) return false;

    const initialValues = initialOtherSectionsValuesRef.current;

    // Compare each target field's current value with initial value
    return targetFieldNames.some((fieldName) => {
      const currentValue = watchedValues[fieldName];
      const initialValue = initialValues[fieldName];

      // Both undefined/null/empty means no change (initial state)
      if (
        initialValue === undefined &&
        (currentValue === undefined || currentValue === null || currentValue === '')
      ) {
        return false;
      }

      // If initial was undefined/null/empty but current has a value, that's a change
      if (
        initialValue === undefined &&
        currentValue !== undefined &&
        currentValue !== null &&
        currentValue !== ''
      ) {
        return true;
      }

      // Deep compare current vs initial
      return !deepEqual(currentValue, initialValue);
    });
  }, [watchedValues, formDataParsed, getTargetSectionFieldNames, deepEqual]);

  // Handle revert changes - reset form values back to initial state for target sections
  const handleRevertChanges = useCallback(() => {
    const initialValues = initialOtherSectionsValuesRef.current;
    const targetFieldNames = getTargetSectionFieldNames();

    // Reset each target field to its initial value
    targetFieldNames.forEach((fieldName) => {
      const initialValue = initialValues[fieldName];
      setValue(fieldName, initialValue, { shouldDirty: false });
    });

    toast.success(t('changesRevertedSuccess', { defaultValue: 'Changes reverted to original values' }));
  }, [getTargetSectionFieldNames, setValue, t]);

  // MERGE SOURCE: defaultData (server state) + watchedValues (form state)
  // watchedValues should override defaults if present
  const disableSourceValues = useMemo(
    () => ({ ...(defaultData || {}), ...(watchedValues || {}) }),
    [defaultData, watchedValues]
  );

  // read disable_condition in either naming style
  const dc = (formDataParsed?.disable_condition as any) || {};
  const disableFieldName = dc.fieldName ?? dc.conditionField;
  const disableFieldValue = dc.fieldValue ?? dc.conditionValue;

  // Evaluate conditional disable against merged values (fix)
  const meetsDisableCondition =
    !!formDataParsed?.conditional_disable_form &&
    !!disableFieldName &&
    isConditionMet(
      { fieldName: disableFieldName, fieldValue: disableFieldValue },
      disableSourceValues
    );

  // final disabled flag used everywhere
  const finalFormDisabled = meetsDisableCondition || isFormDisabled;

  const initialEmailRef = useRef<string>(defaultValues?.Email ?? '');
  useEffect(() => {
    initialEmailRef.current = defaultValues?.Email ?? '';
  }, [defaultValues?.Email]);

  function getLabelForField(fieldName: string, formData: FormData): string {
    if (!formData || !formData.sections) return fieldName;

    // Use array methods only, no loops to comply with no-restricted-syntax
    const allFields = formData.sections.flatMap((section) => section.fields || []);
    const field = allFields.find((f) => f.name === fieldName);

    return field ? field.label || field.heading || fieldName : fieldName;
  }

  useEffect(() => {
    if (!methods.formState.errors || !formDataParsed) return;

    const errorEntries = Object.entries(methods.formState.errors).filter(
      ([, error]) => !!error?.message
    );

    // Create toast promises with staggered delays to avoid race issues
    const toastPromises = errorEntries.map(([field, error], index) => {
      return new Promise<void>((resolve) => {
        setTimeout(() => {
          const label = getLabelForField(field, formDataParsed);
          toast.error(`${t(label, { defaultValue: humanizeLabelKey(label || '') })}: ${error?.message || t('errorLabel', { defaultValue: 'Error' })}`, { id: `${field}-${error?.message}` });
          resolve();
        }, index * 100);
      });
    });

    Promise.all(toastPromises);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [methods.formState.errors, formDataParsed]);

  // When language changes: clear displayed errors so user doesn't see stale messages in the old language.
  // Safe: does not reset form values or refetch; FormBuilder uses ref-based resolver so next validation uses new schema.
  useEffect(() => {
    clearErrors();
  }, [currentLang?.value, clearErrors]);

  // When base schema changes (e.g. after language change in FormLoader), refresh filtered schema so next validation uses new messages.
  // Safe: only updates filtered schema state; resolver reads schemaRef.current at validation time so no re-init of useForm needed.
  useEffect(() => {
    if (!schema || !formDataParsed) return;
    setFilteredFormSchema(getFilteredSchema(methods.getValues(), schema, formDataParsed));
  }, [schema, formDataParsed]); // eslint-disable-line react-hooks/exhaustive-deps -- only run when schema/formDataParsed change; methods.getValues is stable

  // console.log(formDataParsed, 'formDataParsed');

  // dynamic schema update
  const updateSchema = useMemo(() => {
    const fn = debounce((values: Record<string, any>) => {
      const updated = getFilteredSchema(values, schema, formDataParsed);
      setFilteredFormSchema(updated);
    }, 300);

    return fn;
  }, [schema, formDataParsed]);

  // cancel debounced calls on unmount or when deps change
  useEffect(() => {
    return () => updateSchema.cancel();
  }, [updateSchema]);

  const originalOptionsRef = useRef<Record<string, any[]>>({});
  useEffect(() => {
    const subscription = watch((values: any) => {
      updateSchema(values);

      // reset OTP when email changes
      const currentEmail = values.Email;
      if (
        (otpVerified || otpSent) &&
        verifiedEmailRef.current &&
        currentEmail !== verifiedEmailRef.current
      ) {
        setOtpVerified(false);
        setOtpSent(false);
        setVerifyLoading(false);
        setResendTimer(0);
        verifiedEmailRef.current = null;
        if (methods.getValues('EmailOTP')) methods.setValue('EmailOTP', '');
      }

      // handle dependent options
      formDataParsed.sections?.forEach((section) => {
        section.fields?.forEach((field: any) => {
          if (field.depends_on_field && field.filter_options_with_col) {
            const dependentValue = values[field.depends_on_field];
            if (!originalOptionsRef.current[field.name] && field.options) {
              originalOptionsRef.current[field.name] = [...field.options];
            }
            if (dependentValue !== undefined) {
              field.options = originalOptionsRef.current[field.name]?.filter(
                (option) =>
                  option[field.filter_options_with_col as keyof typeof option] === dependentValue
              );
            } else {
              field.options = [...(originalOptionsRef.current[field.name] || [])];
            }
          }
        });
      });
    });
    return () => subscription.unsubscribe();
  }, [watch, updateSchema, formDataParsed, otpVerified, methods, otpSent]);

  const scrollToSection = (sectionId: string) => {
    const section = sectionRefs.current[sectionId];
    if (section) section.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  useEffect(() => {
    const handleHashChange = () => {
      const sectionId = window.location.hash.replace('#', '');
      scrollToSection(sectionId);
    };
    handleHashChange();
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (resendTimer > 0) {
      timer = setInterval(() => setResendTimer((prev) => prev - 1), 1000);
    }
    return () => clearInterval(timer);
  }, [resendTimer]);

  const emailWatchedValue = watch('Email');
  const emailChanged = (emailWatchedValue ?? '') !== (initialEmailRef.current ?? '');

  useEffect(() => {
    // console.log(errors, 'errors from onSubmit');
  }, [errors]);

  useEffect(() => {
    // eslint-disable-next-line consistent-return
    const validateEmail = async () => {
      const emailField = formDataParsed?.sections
        ?.flatMap((s) => s?.fields)
        ?.find((f) => f?.type === FormConstants.Field_Types.EMAIL);
      if (!emailField) return setIsEmailValid(false);
      const emailName = emailField?.name;
      const email = methods?.getValues(emailName);
      if (email && emailName) {
        const ok = await methods.trigger(emailName);
        setIsEmailValid(ok);
      } else {
        setIsEmailValid(false);
      }
    };
    validateEmail();
  }, [emailWatchedValue, formDataParsed.sections, methods]);

  const checkUsernameAvailability = async (loginEmail: string, sqlData: any) => {
    if (!loginEmail || !sqlData) return;
    setCheckingUsername(true);
    try {
      const data = { sql_data: sqlData, loginEmail };
      const encryptRequestData = EncryptUtils.encryptedData(data);
      const res = await checkUserName({ tk: encryptRequestData });
      if (res?.available === false) {
        setIsUsernameAvailable(false);
        setError('loginEmail', { type: 'manual', message: 'Username already exists.' });
      } else {
        setIsUsernameAvailable(true);
        clearErrors('loginEmail');
      }
    } catch (err: any) {
      toast.error(t('validateUsernameFailed', { defaultValue: 'Failed to validate username' }));
      setIsUsernameAvailable(null);
    } finally {
      setCheckingUsername(false);
    }
  };

  const onSignInSubmit = async (data: any) => {
    try {
      data.password = EncryptUtils.encryptedData(data.password);
      data.confirmPassword = EncryptUtils.encryptedData(data.confirmPassword);

      const payload = {
        ...data,
        emailTemplates: formDataParsed?.emailTemplates || [],
        notification_sql_data: formDataParsed?.notification_sql_data,
        is_notification_enabled: formDataParsed?.is_notification_enabled,
        is_admin: isAdmin,
        preApplicationNeeded: signupType === 'new',
      };

      // console.log(payload, 'payload');

      const encryptRequestData = EncryptUtils.encryptedData(payload);
      const res = await signUpService({ tk: encryptRequestData });
      const resObj = JSON.parse(JSON.stringify(res));
      if (resObj?.success) {
        toast.success(
          resObj?.message ||
            'Your account has been created successfully. Please log in to continue.'
        );
        setDialogMessage(
          'Your account has been created successfully, and your pre-application has been submitted. Please log in to update your account or check your application status.'
        );
        setDialogOpen(true);
      }
    } catch (error: any) {
      const serverMessage =
        error?.response?.data?.details ||
        error?.errMessage ||
        error?.message ||
        'Something went wrong. Please try again.';
      toast.error(serverMessage);
    }
  };

  // console.log(defaultData, bannerDetails, 'default and banner details');

  const onSubmit = handleSubmit(async (data: any) => {
    if (emailChanged && !otpVerified && formDataParsed?.enable_otp_verification) {
      toast.error(t('verifyEmailOtpFirst', { defaultValue: 'Please verify your email with OTP before proceeding.' }));
      return;
    }

    const hasRequirementsSection = formDataParsed?.sections?.some(
      (s: any) => s.is_upload_section === true
    );
    if (hasRequirementsSection && !requirementsValid) {
      toast.error(t('uploadRequiredDocsFirst', { defaultValue: 'Please upload all required documents in the Requirements section before submitting.' }));
      return;
    }

    // Prevent submission if image is still uploading
    if (isImageUploading) {
      toast.error(t('waitForImageUpload', { defaultValue: 'Please wait for the image upload to complete before submitting.' }));
      return;
    }

    try {
      if (!module_name) return;


      const originalWageEarnersList = data.wageEarnersList;
      if (Array.isArray(data.wageEarnersList))
        data.wageEarnersList = { wageEarnersList: data.wageEarnersList };

      const originalTenants = data.Tenants;
      if (Array.isArray(data.Tenants)) data.Tenants = { Tenants: data.Tenants };

      // Upload any pending images before processing form data
      const uploadPromises: Promise<void>[] = [];
      imageUploadFunctionsRef.current.forEach((uploadFn, fieldName) => {
        uploadPromises.push(
          uploadFn()
            .then((uploadedValue) => {
              if (uploadedValue && (data[fieldName] === '__PENDING_UPLOAD__' || !data[fieldName])) {
                data[fieldName] = uploadedValue;
                // Update form value
                methods.setValue(fieldName, uploadedValue);
              }
            })
            .catch((error) => {
              toast.error(`Failed to upload image: ${error.message}`);
              throw error; // Stop form submission if upload fails
            })
        );
      });

      // Wait for all image uploads to complete
      if (uploadPromises.length > 0) {
        setIsImageUploading(true);
        try {
          await Promise.all(uploadPromises);
        } finally {
          setIsImageUploading(false);
        }
      }

      const normalizeValue: any = (value: any) => {
        if (Array.isArray(value)) return value.map(normalizeValue);
        if (value !== null && typeof value === 'object') {
          return Object.fromEntries(
            Object.entries(value).map(([k, v]) => [k, normalizeValue(v === undefined ? null : v)])
          );
        }
        return value === undefined ? null : value;
      };
      const normalizedData = normalizeValue(data);
      
      // Ensure bg_image is always included in payload (even if empty) for system-settings form
      if (module_name === 'control-panel' && sub_module === 'system-settings') {
        if (!Object.prototype.hasOwnProperty.call(normalizedData, 'bg_image')) {
          normalizedData.bg_image = data.bg_image || '';
        }
        // Remove pending upload markers
        if (normalizedData.bg_image === '__PENDING_UPLOAD__') {
          normalizedData.bg_image = '';
        }
      }

      if (module_name === 'sign-up') {
        await onSignInSubmit?.(data);
        return;
      }

      const templateData = { ...bannerDetails, ...defaultData, ...data };
      let subject: string | undefined;
      let body: string | undefined;
      const template = formDataParsed?.emailTemplate;
      if (template && template?.subject && template?.body) {
        subject = injectTemplateFields(template.subject, template.fields, templateData);
        body = injectTemplateFields(template.body, template.fields, templateData);
      }
      // console.log(template, 'template');
      // console.log(data, 'onSubmit data');

      const token_person_id = tokenData?.payload?.user?.PersonId;
      let payload = {
        sql_data: formDataParsed.submit_button_sql
          ? formDataParsed.submit_button_sql
          : isUpdateMode
            ? formDataParsed.submit_button_update_sql
            : formDataParsed.submit_button_insert_sql,
        notification_sql_data: formDataParsed?.notification_sql_data,
        is_notification_enabled: formDataParsed?.is_notification_enabled,
        case_id: isAdmin
          ? paramsData.CaseId || paramsData.CaseID
          : defaultData.CaseId || bannerDetails?.FullApplicationCaseId,
        person_id: defaultData.PersonId || token_person_id,
        record_id: defaultData.RecordId,
        is_admin: isAdmin,
        action_type: isUpdateMode ? 'update' : 'submit',
        ...(isUpdateMode && {
          emailTemplate: { subject, body },
          encrypted_key_on_submit: formDataParsed?.encrypted_key_on_submit,
          get_raw_data_sql: formDataParsed?.get_raw_data_sql,
        }),
        ...data,
      };
      // console.log(payload, 'payload');
      let response;
      if (isAdmin) {
        if (!sub_module) {
          return;
        }
        
        payload = {
          ...payload,
          ...paramsData,
          admin_person_id: (decodedToken as any)?.payload?.PersonId,
        };
        if (module_name === 'control-panel' && sub_module === 'landing-page-content' && landingPageLang) {
          payload.lang = landingPageLang;
        }
        if (module_name === 'control-panel' && sub_module === 'system-settings' && systemSettingsLang) {
          payload.lang = systemSettingsLang;
        }
          try {
            response = await executeAdminActions(module_name!, sub_module!, {
              tk: EncryptUtils.encryptedData(payload),
            });
            
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            response
              ? toast.success(t('changesUpdatedSuccess', { defaultValue: 'Changes successfully updated' }))
              : toast.error(t('submitFormError', { defaultValue: 'An error occurred while submitting the form.' }));
          } catch (error) {
            toast.error(t('submitFormError', { defaultValue: 'An error occurred while submitting the form.' }));
          }
      } else {
        payload = {
          ...payload,
          ...paramsData,
          ...bannerDetails,
          FirstName: data.FirstName,
          LastName: data.LastName,
          MiddleName: data.MiddleName,
        };
        response = await executeUserActions(module_name!, {
          tk: EncryptUtils.encryptedData(payload),
        });
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        response
          ? toast.success(t('changesUpdatedSuccess', { defaultValue: 'Changes successfully updated' }))
          : toast.error(t('submitFormError', { defaultValue: 'An error occurred while submitting the form.' }));
      }

      if (response && refreshForm) refreshForm();
      if (response && !isAdmin && bannerDetails) {
        setBannerDetails({
          ...bannerDetails,
          FirstName: data.FirstName,
          LastName: data.LastName,
          MiddleName: data.MiddleName,
        });
      }

      // Reset initial values for other sections after successful save to allow properties editing
      if (response) {
        const targetFieldNames = getTargetSectionFieldNames();
        const newInitialValues: Record<string, any> = {};
        targetFieldNames.forEach((fieldName) => {
          const currentValue = watchedValues[fieldName];
          // Store value even if undefined to track initial state
          if (typeof currentValue === 'object' && currentValue !== null) {
            try {
              newInitialValues[fieldName] = JSON.parse(JSON.stringify(currentValue));
            } catch {
              newInitialValues[fieldName] = currentValue;
            }
          } else {
            newInitialValues[fieldName] = currentValue;
          }
        });
        initialOtherSectionsValuesRef.current = newInitialValues;
        initialValuesCapturedRef.current = true; // Ensure flag remains true after save
      }

      if (formDataParsed?.encrypted_key_on_submit)
        filteredFormSchema.parse({
          ...data,
          wageEarnersList: originalWageEarnersList,
          Tenants: originalTenants,
        });
    } catch (error: any) {
      toast.error(error.message || 'An error occurred while submitting the form.');
      refreshForm();
      if (error instanceof zod.ZodError) {
        error.errors.forEach(() => {
          /* no-op */
        });
      } else {
        console.error('Unexpected error:', error);
      }
    }
  });

  const handleDialogClose = () => {
    setDialogOpen(false);
    navigate('/sign-in');
  };

  useEffect(() => {
    if (Object.keys(defaultValues).length > 0) methods.reset(defaultValues);
  }, [defaultValues, methods]);

  const handleRescindApplication = async () => {
    if (!module_name) return;
    // Determine application type
    const isPreApplication = formDataParsed?.name === 'pre-application';
    const applicationType = isPreApplication ? 'pre-application' : 'full application';

    try {
      const payload: any = {
        sql_data: formDataParsed?.rescind_button_sql,
        notification_sql_data: formDataParsed?.notification_sql_data,
        is_notification_enabled: formDataParsed?.is_notification_enabled,
        case_id: defaultData.CaseId,
        person_id: defaultData.PersonId,
        action_type: 'rescind',
        is_admin: isAdmin,
      };
      await executeUserActions(module_name!, { tk: EncryptUtils.encryptedData(payload) });

      toast.success(`Your ${applicationType} has been successfully deleted.`);

      // Refresh current tab
      if (refreshForm) await refreshForm();
      // Refresh full application status
      if (onRefreshFullApplicationStatus) await onRefreshFullApplicationStatus();
      // Refresh banner values first, then trigger properties refresh
      const token_person_id = tokenData?.payload?.user?.PersonId;
      if (token_person_id) {
        try {
          const bannerPayload = { PersonId: token_person_id };
          const encryptedBannerPayload = EncryptUtils.encryptedData(bannerPayload);
          const bannerData = await getBannerDetails({ tk: encryptedBannerPayload });
          setBannerDetails(bannerData);
        } catch (bannerError) {
          console.error('Failed to refresh banner details:', bannerError);
        }
      }
      // Clear properties after banner update (for full application rescind only)
      if (!isPreApplication) {
        setClearPropertiesOnRescind((prev) => prev + 1);
      }
    } catch (error: any) {
      toast.error(error.message || `Failed to delete the ${applicationType}.`);
    }
  };

  const renderInputs = ({
    field,
    fieldSx,
    inputSx,
    key,
    sectionDisabled = false,
    sectionLabel,
  }: CustomInputProps) => {
    // Disable form fields if DETAILS_EDIT permission is missing for entities/properties/person-details
    // Only apply permission checks for admin users
    const isDetailsReadOnly = isAdmin && (module_name === 'entities' || module_name === 'properties' || module_name === 'person-details') && !canEditDetails;
    const disabledAll = finalFormDisabled || isDetailsReadOnly;

    const renderHeadings = () => (
      <>
        {field.heading && (
          <Typography
            variant="h6"
            my={field.sub_heading ? 1 : 0}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              ...maxLine({ line: 2, persistent: theme.typography.h6 }),
            }}
          >
            {t(field.heading, { defaultValue: humanizeLabelKey(field.heading || '') })}
            {field.required && (!field.label || field.label.trim() === '') && (
              <Box component="span" sx={{ color: 'text.secondary', ml: 0.5 }}>
                *
              </Box>
            )}
            {(field.type === 'radio_group' ||
              field.type === 'select' ||
              (field.type === 'text' && field.name === 'SoldAssetsAmount') ||
              field.is_heading_only_block) &&
              field.helper_text && (
                <Tooltip infoButton title={t(field.helper_text, { defaultValue: humanizeLabelKey(field.helper_text || '') })}>
                  <Icon icon="si:info-fill" />
                </Tooltip>
              )}
          </Typography>
        )}
        {field.sub_heading && (
          <Typography variant="body2" my={1}>
            {t(field.sub_heading, { defaultValue: humanizeLabelKey(field.sub_heading || '') })}
          </Typography>
        )}
      </>
    );

    const renderField = () => {
      switch (field.type) {
        case FormConstants.Field_Types.TEXT: {
          const isUsernameField = field.name === 'loginEmail';
          const sqlData = field.sql_data;
          return (
            <Field.Text
              variant="filled"
              disabled={field.disabled || disabledAll || sectionDisabled}
              required={field.required}
              sx={inputSx}
              label={t(field.label, { defaultValue: humanizeLabelKey(field.label || '') })}
              name={field.name}
              type={
                field.validation_schema === 'email'
                  ? 'email'
                  : field.validation_schema === 'phoneNumber'
                    ? 'tel'
                    : 'text' // Always use 'text' instead of 'number'
              }
              inputMode={
                field.validation_schema === 'number' || field.validation_schema === 'floatNumber'
                  ? 'numeric' // This gives mobile users numeric keypad
                  : undefined
              }
              placeholder={t(field.placeholder || '', { defaultValue: humanizeLabelKey(field.placeholder || '') || '' })}
              onBlur={async (e) => {
                const value = e.target.value?.trim();
                if (isUsernameField && value && sqlData)
                  await checkUsernameAvailability(value, sqlData);
              }}
              onWheel={(e) => {
                // Prevent scroll from changing number values
                if (
                  field.validation_schema === 'number' ||
                  field.validation_schema === 'floatNumber'
                ) {
                  (e.target as HTMLInputElement).blur();
                }
              }}
              onKeyDown={(e) => {
                // Only allow numeric input for number fields
                if (
                  field.validation_schema === 'number' ||
                  field.validation_schema === 'floatNumber'
                ) {
                  const target = e.target as HTMLInputElement;
                  const allowedKeys = [
                    'Backspace',
                    'Delete',
                    'Tab',
                    'Escape',
                    'Enter',
                    'ArrowLeft',
                    'ArrowRight',
                    'ArrowUp',
                    'ArrowDown',
                    'Home',
                    'End',
                  ];

                  const isNumber = /^[0-9]$/.test(e.key);
                  const isDecimal = e.key === '.' && field.validation_schema === 'floatNumber';
                  const isMinus = e.key === '-' && target.selectionStart === 0; // Allow minus at start

                  if (!allowedKeys.includes(e.key) && !isNumber && !isDecimal && !isMinus) {
                    e.preventDefault();
                  }

                  // Prevent multiple decimal points
                  if (isDecimal && target.value.includes('.')) {
                    e.preventDefault();
                  }
                }
              }}
              InputProps={{
                endAdornment: isUsernameField ? (
                  <InputAdornment position="end">
                    {checkingUsername ? (
                      <Iconify icon="eos-icons:loading" />
                    ) : isUsernameAvailable === true ? (
                      <Iconify icon="eva:checkmark-circle-2-fill" color="green" />
                    ) : isUsernameAvailable === false ? (
                      <Iconify icon="mdi:close-circle" color="red" />
                    ) : null}
                  </InputAdornment>
                ) : undefined,
              }}
            />
          );
        }

        case FormConstants.Field_Types.EMAIL: {
          const { getValues } = methods;
          const otpConfig = field?.otp;
          const otpValue = watch(otpConfig?.otp_name);

          const handleSendOtp = async () => {
            try {
              if (!emailChanged) return;
              setSendOtpLoading(true);
              const email = getValues(field.name);
              const firstName = getValues('FirstName');
              const middleName = getValues('MiddleName');
              const lastName = getValues('LastName');
              if (!email) {
                toast.error(t('enterEmailFirst', { defaultValue: 'Please enter your email first' }));
                return;
              }
              const generatedOTP = Math.floor(100000 + Math.random() * 900000).toString();
              const fullName = [firstName, middleName, lastName].filter(Boolean).join(' ').trim();

              const data = {
                sql_data: otpConfig?.send_otp_sql_data,
                Email: email,
                EmailOTP: generatedOTP,
                FullName: fullName,
                module_name,
              };

              const template = otpConfig?.otp_email_template;
              // Translate template fields if they are translation keys
              const templateSubject = template?.subject ? t(template.subject) : template?.subject;
              const templateBody = template?.body ? t(template.body) : template?.body;
              const subject = injectTemplateFields(templateSubject, template?.fields, data);
              const body = injectTemplateFields(templateBody, template?.fields, data);

              const payload: any = { ...data, emailTemplate: { subject, body } };
              const encryptRequestData = EncryptUtils.encryptedData(payload);
              const res = await sendOtp({ tk: encryptRequestData });

              if (res?.success === false) {
                toast.warning(
                  res?.message ||
                    'You’ve already requested an OTP recently. Please wait a few minutes before trying again.'
                );
                setSendOtpLoading(false);
                return;
              }

              if (res.success) {
                toast.success(res?.message || 'OTP sent to your email');
                setOtpSent(true);
                setResendTimer(otpConfig.resend_otp_wait_seconds || 300);
                setSendOtpLoading(false);
              }
            } catch (err: any) {
              toast.error(err.message || 'Failed to send OTP');
              setSendOtpLoading(false);
            }
          };

          const handleVerifyOtp = async () => {
            if (
              !otpConfig.verify_otp_sql_data ||
              !getValues(otpConfig.otp_name) ||
              !getValues(field.name)
            )
              return;
            try {
              setVerifyLoading(true);
              const data = {
                sql_data: otpConfig?.verify_otp_sql_data,
                Email: getValues(field.name),
                EmailOTP: getValues(otpConfig.otp_name),
              };
              const encryptRequestData = EncryptUtils.encryptedData(data);
              const res = await verifyOtp({ tk: encryptRequestData });
              if (res.success) {
                toast.success(res?.message || 'OTP verified successfully');
                setOtpVerified(true);
                setVerifyLoading(false);
                setResendTimer(0);
                verifiedEmailRef.current = getValues(field.name);
              }
            } catch (err: any) {
              if (err?.response?.status === 410)
                toast.error(err.message || 'OTP has expired. Please request a new one.');
              else if (err?.response?.status === 401)
                toast.error(err.message || 'Invalid OTP. Please try again.');
              else toast.error(err.message || 'OTP verification failed');
              setVerifyLoading(false);
            }
          };

          return (
            <Stack spacing={2} key={key}>
              <Field.Text
                variant="filled"
                type="email"
                disabled={finalFormDisabled || sectionDisabled}
                required={field.required}
                sx={inputSx}
                label={t(field.label, { defaultValue: humanizeLabelKey(field.label || '') })}
                name={field.name}
                placeholder={t(field.placeholder || '', { defaultValue: humanizeLabelKey(field.placeholder || '') || '' })}
                InputProps={{
                  endAdornment:
                    otpVerified && emailChanged ? (
                      <InputAdornment position="end">
                        <Iconify icon="eva:checkmark-circle-2-fill" color="green" />
                      </InputAdornment>
                    ) : undefined,
                }}
              />

              {emailChanged && !otpVerified && (
                <>
                  {otpSent && isEmailValid && (
                    <>
                      <Typography variant="body2">{t(otpConfig.otp_label || '')}</Typography>
                      <RHFCode name={otpConfig.otp_name} autoFocus={false} />
                    </>
                  )}

                  {isEmailValid && (
                    <Stack direction="row" spacing={2}>
                      <LoadingButton
                        variant="outlined"
                        onClick={handleSendOtp}
                        loading={sendOtpLoading}
                        disabled={resendTimer > 0}
                      >
                        {resendTimer > 0
                          ? `Resend in ${resendTimer}s`
                          : t(otpConfig.send_otp_button_text || '')}
                      </LoadingButton>

                      {otpSent && (
                        <LoadingButton
                          variant="contained"
                          onClick={handleVerifyOtp}
                          loading={verifyLoading}
                          disabled={!otpValue || otpValue.trim().length !== 6}
                        >
                          {t(otpConfig.verify_otp_button_text || '')}
                        </LoadingButton>
                      )}
                    </Stack>
                  )}
                </>
              )}
            </Stack>
          );
        }

        case FormConstants.Field_Types.PHONE:
          return (
            <Field.Phone
              variant="filled"
              disabled={field.disabled || finalFormDisabled || sectionDisabled}
              required={field.required}
              label={t(field.label, { defaultValue: humanizeLabelKey(field.label || '') })}
              name={field.name}
            />
          );

        case FormConstants.Field_Types.PASSWORD:
          return (
            <Field.Text
              variant="filled"
              disabled={finalFormDisabled || sectionDisabled}
              required={field.required}
              label={t(field.label, { defaultValue: humanizeLabelKey(field.label || '') })}
              name={field.name}
              type={
                password.value ? FormConstants.Field_Types.TEXT : FormConstants.Field_Types.PASSWORD
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={password.onToggle} edge="end">
                      <Iconify icon={password.value ? 'solar:eye-bold' : 'solar:eye-closed-bold'} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          );

        case FormConstants.Field_Types.SELECT:
          return (
            <Field.Select
              variant="filled"
              disabled={field.disabled || finalFormDisabled || sectionDisabled}
              sx={inputSx}
              name={field.name}
              label={field.label ? t(field.label, { defaultValue: humanizeLabelKey(field.label) }) : undefined}
              required={field.required}
              InputLabelProps={
                sectionLabel === 'personalInformation'
                  ? { shrink: true }
                  : { shrink: false, style: { display: 'none' } }
              }
            >
              <MenuItem value="">{tCommon('noneLabel', { defaultValue: 'None' })}</MenuItem>
              <Divider sx={{ borderStyle: 'dashed' }} />
              {field?.options?.map((option: any) => (
                <MenuItem key={option.value} sx={{ height: '100%' }} value={option.value}>
                  {t(option.label, { defaultValue: humanizeLabelKey(option.label || '') })}
                </MenuItem>
              ))}
            </Field.Select>
          );

        case FormConstants.Field_Types.COUNTRY:
          return (
            <Field.CountrySelect
              variant="filled"
              label={t(field.label, { defaultValue: humanizeLabelKey(field.label || '') })}
              required={field.required}
              name={field.name}
              disabled={finalFormDisabled || sectionDisabled}
            />
          );

        case FormConstants.Field_Types.RADIO_GROUP:
          return (
            <Field.RadioGroup
              sx={{ paddingX: 2 }}
              label={t(field.label, { defaultValue: humanizeLabelKey(field.label || '') })}
              aria-required={field.required}
              area-disabled={field.disabled}
              valueType={field.validationSchema}
              name={field.name}
              options={(field.options || []).map((option: any) => {
                // Translate "Yes" and "No" labels using translation keys
                let translatedLabel = option.label;
                if (option.label === 'Yes') {
                  translatedLabel = t('yes', { defaultValue: 'Yes' });
                } else if (option.label === 'No') {
                  translatedLabel = t('no', { defaultValue: 'No' });
                } else {
                  translatedLabel = t(option.label, { defaultValue: humanizeLabelKey(option.label || '') });
                }
                return {
                  ...option,
                  label: translatedLabel,
                };
              })}
              disabled={finalFormDisabled || sectionDisabled}
            />
          );

        case FormConstants.Field_Types.TABLE_INPUT:
          return (
            <Field.TableInput
              columns={field.columns || []}
              name={field.name}
              disabled={(finalFormDisabled ?? false) || sectionDisabled}
              delete_sql={field.delete_sql}
            />
          );

        case FormConstants.Field_Types.TEXT_AREA:
          return (
            <Field.Text
              variant="filled"
              disabled={field.disabled || finalFormDisabled || sectionDisabled}
              required={field.required}
              sx={inputSx}
              label={t(field.label, { defaultValue: humanizeLabelKey(field.label || '') })}
              fullWidth
              rows={4}
              name={field.name}
              placeholder={t(field.placeholder || '', { defaultValue: humanizeLabelKey(field.placeholder || '') || '' })}
            />
          );

        case FormConstants.Field_Types.UPLOAD_AREA:
          return (
            <UploadArea
              field={field}
              disabled={finalFormDisabled || sectionDisabled}
              module_name={module_name}
              sub_module={sub_module}
              paramsData={paramsData}
              isAdmin={isAdmin}
              defaultData={defaultData}
              bannerDetails={bannerDetails}
            />
          );

        case FormConstants.Field_Types.EDITOR:
          return (
            <Box sx={{ width: '100%', mb: 3 }}>
              <Field.Editor 
                name={field.name} 
                sx={{ 
                  width: '100%', 
                  maxWidth: '100%',
                  '& .tiptap': {
                    minHeight: '400px'
                  }
                }} 
              />
            </Box>
          );

        case FormConstants.Field_Types.DATE_INPUT:
          // Or use 'date_input' if not defined in FormConstants
          return (
            <Controller
              control={methods.control}
              name={field.name}
              render={({ field: controllerField, fieldState }) => (
                <DatePicker
                  label={t(field.label, { defaultValue: humanizeLabelKey(field.label || '') })}
                  value={controllerField.value ? dayjs(controllerField.value) : null}
                  onChange={(date) => {
                    if (!date || !dayjs(date).isValid()) {
                      controllerField.onChange(null);
                      return;
                    }
                    // Store ONLY date part as formatted local string, no time or TZ
                    controllerField.onChange(date.format('YYYY-MM-DD'));
                  }}
                  slotProps={{
                    textField: {
                      variant: 'filled',
                      sx: inputSx,
                      error: !!fieldState.error,
                      helperText: fieldState.error?.message,
                      disabled: finalFormDisabled || sectionDisabled,
                      required: field.required,
                    },
                  }}
                />
              )}
            />
          );

        case FormConstants.Field_Types.DATE_TIME_INPUT:
          // Or use 'date-time-picker' if not defined in FormConstants
          return (
            <Controller
              control={methods.control}
              name={field.name}
              render={({ field: controllerField, fieldState }) => (
                <DateTimePicker
                  label={t(field.label, { defaultValue: humanizeLabelKey(field.label || '') })}
                  value={controllerField.value ? dayjs(controllerField.value) : null}
                  onChange={(date) => controllerField.onChange(date ? date.toISOString() : null)}
                  slotProps={{
                    textField: {
                      variant: 'filled',
                      sx: inputSx,
                      error: !!fieldState.error,
                      helperText: fieldState.error?.message,
                      disabled: finalFormDisabled || sectionDisabled,
                      required: field.required,
                    },
                  }}
                />
              )}
            />
          );

        case FormConstants.Field_Types.IMAGE_UPLOAD_PREVIEW:
          return (
            <Controller
              control={methods.control}
              name={field.name}
              render={({ field: controllerField }) => (
                <ImageUploadPreview
                  value={controllerField.value}
                  onChange={controllerField.onChange}
                  label={t(field.label, { defaultValue: humanizeLabelKey(field.label || '') })}
                  helperText={field.helper_text}
                  disabled={finalFormDisabled || sectionDisabled}
                  onUploadStateChange={setIsImageUploading}
                  onUploadReady={(uploadFn: () => Promise<string | null>) => {
                    imageUploadFunctionsRef.current.set(field.name, uploadFn);
                  }}
                />
              )}
            />
          );

        default:
          return <></>;
      }
    };

    return (
      <Box key={key} sx={fieldSx}>
        {/* headings */}
        <>
          {field.heading && (
            <Typography
              variant="h6"
              my={field.sub_heading ? 1 : 0}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                ...maxLine({ line: 2, persistent: theme.typography.h6 }),
              }}
            >
              {t(field.heading, { defaultValue: humanizeLabelKey(field.heading || '') })}
              {field.required && (!field.label || field.label.trim() === '') && (
                <Box component="span" sx={{ color: 'text.secondary', ml: 0.5 }}>
                  *
                </Box>
              )}
              {(field.type === 'radio_group' ||
                field.type === 'select' ||
                (field.type === 'text' && field.name === 'SoldAssetsAmount') ||
                field.is_heading_only_block) &&
                field.helper_text && (
                  <Tooltip infoButton title={t(field.helper_text, { defaultValue: humanizeLabelKey(field.helper_text || '') })}>
                    <Icon icon="si:info-fill" />
                  </Tooltip>
                )}
            </Typography>
          )}
          {field.sub_heading && (
            <Typography variant="body2" my={1}>
              {t(field.sub_heading, { defaultValue: humanizeLabelKey(field.sub_heading || '') })}
            </Typography>
          )}
        </>
        {/* field */}
        <Tooltip disableHoverListener title={t(field.helper_text || '', { defaultValue: humanizeLabelKey(field.helper_text || '') || '' })}>
          {renderField()}
        </Tooltip>
      </Box>
    );
  };

  const renderFields = (
    <Box>
      {stageMessage && (
        <Box
          bgcolor={theme.palette.background.paper}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            position: 'sticky',
            width: '100%',
            zIndex: 99,
            top: 0,
            left: 0,
          }}
        >
          <Alert severity="warning">
            <AlertTitle>{t('stageMessageLabel', { defaultValue: 'Stage Message' })}</AlertTitle>
            {stageMessage}
          </Alert>
        </Box>
      )}

      {formDataParsed?.sections?.map((section: any, index) => (
        <React.Fragment key={index}>
          {section.type === 'custom' && section.component === 'RequirementsManagementView' ? (
            <Box sx={{ mb: 3 }}>
              <RequirementsManagementView />
            </Box>
          ) : section.is_upload_section ? (
            section.conditionalRender &&
            section.condition &&
            !(Array.isArray(section.condition.fieldValue)
              ? section.condition.fieldValue.includes(watchedValues[section.condition.fieldName])
              : String(watchedValues[section.condition.fieldName]) ===
                String(section.condition.fieldValue)) ? null : (
              <Box>
                <RequirementUploader
                  uiData={section}
                  disabled={(finalFormDisabled ?? false) || shouldDisableSection(section)}
                  module_name={module_name || ''}
                  paramsData={paramsData}
                  isAdmin={isAdmin}
                  defaultData={defaultData}
                  bannerDetails={bannerDetails}
                  onValidationChange={setRequirementsValid}
                />
              </Box>
            )
          ) : section.fields.some((field: any) => field.type === 'dnd') ? (
            <DndPropertiesSelector
              uiData={section as any}
              disabled={finalFormDisabled ?? false}
              fullApplicationIncomeCategory={fullApplicationIncomeCategory}
              isAdmin={isAdmin}
              module_name={module_name}
              sub_module={sub_module}
              refreshForm={refreshForm}
              setIsCheckingForProperties={setIsCheckingForProperties}
              defaultData={defaultData}
              isOtherSectionsDirty={isOtherSectionsDirty}
              onRevertChanges={handleRevertChanges}
              onSaveChanges={onSubmit}
              clearPropertiesOnRescind={clearPropertiesOnRescind}
            />
          ) : (
            <Box
              ref={(el) => {
                if (section.hash) sectionRefs.current[section.hash] = el as HTMLDivElement;
              }}
              sx={{
                mb: 3,
                // Large spacing after Help URL (end of Section 1) before Background Images (Section 2)
                ...(section.hash === 'help_url' ? { mb: 6 } : {}),
                // Large spacing after Background Images (end of Section 2) before Requirements Management (Section 3)
                ...(section.hash === 'background_images' ? { mb: 6 } : {}),
              }}
            >
              <Typography variant= {module_name === 'control-panel'?"h6":"h4"}>
                {t(section.section_label, { defaultValue: humanizeLabelKey(section.section_label || '') })}
                {shouldDisableSection(section) && (
                  <Typography
                    component="span"
                    variant="body2"
                    color="text.secondary"
                    sx={{ ml: 1 }}
                  >
                    (Please select properties first)
                  </Typography>
                )}
              </Typography>
              <Stack direction="row" spacing={3} my={2} flexWrap="wrap">
                {section?.fields?.map((field: any, idx: any) => {
                  // conditional field render (uses field.condition naming or alternate)
                  const cond = field.condition;
                  const shouldRender =
                    !field.conditionalRender ||
                    !cond ||
                    isConditionMet(
                      {
                        fieldName: cond.fieldName ?? cond.conditionField,
                        fieldValue: cond.fieldValue ?? cond.conditionValue,
                      },
                      disableSourceValues
                    );

                  if (!shouldRender) return null;

                  const isVertical = section.render_vertical;
                  const fieldSx = isVertical
                    ? {
                        width:
                          field.type === 'radio_group' ||
                          field.type === 'table_input' ||
                          field.type === 'text_area' ||
                          field.type === 'editor' ||
                          field.is_heading_only_block
                            ? '100%'
                            : isMobile
                              ? '100%'
                              : '25%',
                      }
                    :  { width: isMobile ? '100%' : '25%' }
                  const inputSx = isVertical ? { maxWidth: '100%', width: '100%' } : {};
                  const sectionDisabled = shouldDisableSection(section);

                  return renderInputs({
                    field,
                    fieldSx,
                    inputSx,
                    key: idx,
                    sectionDisabled,
                    sectionLabel: section.section_label,
                  });
                })}
              </Stack>
            </Box>
          )}
        </React.Fragment>
      ))}

      {/* footer bar */}
      <Box
        sx={{
          position: module_name === 'control-panel' ? 'relative' : 'sticky',
          bottom: 0,
          width: '100%',
          zIndex: 100,
          bgcolor: theme.palette.background.paper,
          borderRadius: 1,
        }}
      >
        {formDataParsed?.showAuditInfo &&
          (!isMobile ? (
            <Card
              elevation={3}
              sx={{
                width: '100%',
                zIndex: 100,
                mt: 4,
                bgcolor: theme.palette.mode === 'dark' ? '#1D242A' : '#F6F7F8',
                borderRadius: 1,
                px: 2,
                borderTop: '1px dotted #ccc',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                <Box sx={{ flex: 1, minWidth: '200px', marginRight: 2, mb: 1 }}>
                  <Typography variant="body1" fontWeight="bold">
                    {t('createdOn', { defaultValue: 'Created On' })}
                  </Typography>
                  <Typography variant="body2">{formatDateTime(defaultData?.CreatedDate)}</Typography>
                </Box>
                <Box sx={{ flex: 1, minWidth: '200px', marginRight: 2, mb: 1 }}>
                  <Typography variant="body1" fontWeight="bold">
                    {t('createdBy', { defaultValue: 'Created By' })}
                  </Typography>
                  <Typography variant="body2">{defaultData?.CreatedBy}</Typography>
                </Box>
                <Box sx={{ flex: 1, minWidth: '200px', marginRight: 2, mb: 1 }}>
                  <Typography variant="body1" fontWeight="bold">
                    {t('modifiedOn', { defaultValue: 'Modified On' })}
                  </Typography>
                  <Typography variant="body2">{formatDateTime(defaultData?.ModifiedDate)}</Typography>
                </Box>
                <Box sx={{ flex: 1, minWidth: '200px', mb: 1 }}>
                  <Typography variant="body1" fontWeight="bold">
                    {t('modifiedBy', { defaultValue: 'Modified By' })}
                  </Typography>
                  <Typography variant="body2">{defaultData?.ModifiedBy}</Typography>
                </Box>
              </Box>
            </Card>
          ) : (
            <Card
              elevation={3}
              sx={{
                width: '100%',
                zIndex: 100,
                mt: 4,
                bgcolor: theme.palette.mode === 'dark' ? '#1D242A' : '#F6F7F8',
                borderRadius: 1,
                px: 2,
                borderTop: '1px dotted #ccc',
              }}
            >
              <Accordion sx={{ width: '100%' }}>
                <AccordionSummary
                  expandIcon={<Iconify icon="si:expand-less-circle-duotone" />}
                  aria-controls="panel1a-content"
                  id="panel1a-header"
                  sx={{ display: 'flex', justifyContent: 'space-between' }}
                >
                  <Typography variant="body1" fontWeight="bold">
                    Created and Modified Details
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body1" fontWeight="bold">
                        {t('createdOn', { defaultValue: 'Created On' })}
                      </Typography>
                      <Typography variant="body2">{formatDateTime(defaultData?.CreatedDate)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body1" fontWeight="bold">
                        {t('createdBy', { defaultValue: 'Created By' })}
                      </Typography>
                      <Typography variant="body2">{defaultData?.CreatedBy}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body1" fontWeight="bold">
                        {t('modifiedOn', { defaultValue: 'Modified On' })}
                      </Typography>
                      <Typography variant="body2">{formatDateTime(defaultData?.ModifiedDate)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body1" fontWeight="bold">
                        {t('modifiedBy', { defaultValue: 'Modified By' })}
                      </Typography>
                      <Typography variant="body2">{defaultData?.ModifiedBy}</Typography>
                    </Box>
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Card>
          ))}

        {formDataParsed?.show_cancel_button === true && !isUpdateMode && (
          <Button
            sx={{ minWidth: 'max-content', width: 150, marginTop: '10px', marginRight: 2 }}
            color="inherit"
            size="large"
            variant="outlined"
            onClick={() => {
              methods.reset(defaultValues);
              toast.info('Form has been reset.');
            }}
          >
            {t('cancelButtonLabel', { defaultValue: 'Cancel' })}
          </Button>
        )}

        {/* Revert button for control-panel tabs - hide if no CONTROL_PANEL:EDIT permission */}
        {module_name === 'control-panel' && sub_module && canEditControlPanel && (
          <LoadingButton
            sx={{ minWidth: 'max-content', width: 150, marginTop: '10px', marginRight: 2 }}
            color="error"
            size="large"
            variant="outlined"
            onClick={() => {
              setRevertDialogOpen(true);
            }}
            startIcon={<Iconify icon="solar:restart-bold" />}
          >
            Revert
          </LoadingButton>
        )}

        {/* Update button - hide if no permission for entities/properties DETAILS_EDIT, or hide for control-panel if no CONTROL_PANEL:EDIT */}
        {(formDataParsed?.terms_acknowledgement || formDataParsed?.submit_button_text) &&
          (module_name === 'control-panel' || hasPermission(formDataParsed?.submit_button_sql)) &&
          // Hide for entities/properties/person-details if no DETAILS_EDIT permission (admin only)
          !(isAdmin && isUpdateMode && (module_name === 'entities' || module_name === 'properties' || module_name === 'person-details') && !canEditDetails) &&
          // Hide for control-panel if no CONTROL_PANEL:EDIT permission
          !(module_name === 'control-panel' && !canEditControlPanel) && (
          <LoadingButton
            sx={{ minWidth: 'max-content', width: 150, marginTop: '10px' }}
            color="inherit"
            size="large"
            type={formDataParsed.terms_acknowledgement ? 'button' : 'submit'}
            onClick={async () => {
              // Permission check: Button is hidden if user doesn't have permission (checked at render)
              // This is a safety check in case permissions change or edge cases
              if (module_name === 'control-panel' && !canEditControlPanel) {
                return;
              }
              
              // Permission check: Button is hidden if user doesn't have permission (checked at render)
              // This is a safety check in case permissions change or edge cases
              if (isAdmin && (module_name === 'entities' || module_name === 'properties' || module_name === 'person-details') && !canEditDetails) {
                return;
              }
              
              const valid = await trigger();
              const email = methods.getValues('Email');

              let hasError = false;

              if (isUsernameAvailable === false) {
                setError('loginEmail', { type: 'manual', message: 'Username already exists.' });
                toast.error(t('usernameExistsError', { defaultValue: 'Username already exists. Please choose another.' }));
                hasError = true;
              }

              if (email && emailChanged && isEmailValid && !otpVerified) {
                setError('Email', {
                  type: 'manual',
                  message: 'Please verify your email using the OTP before proceeding.',
                });
                toast.error(t('verifyEmailOtpFirst', { defaultValue: 'Please verify your email with OTP before proceeding.' }));
                hasError = true;
              }

              if (!valid) hasError = true;
              if (hasError) return;

              if (errors.Email?.type === 'manual' && isEmailValid && otpVerified) {
                clearErrors('Email');
              }

              if (formDataParsed.terms_acknowledgement && valid) {
                dialog.onTrue();
              }
            }}
            variant="contained"
            loading={isSubmitting}
            loadingIndicator={<Iconify icon="eos-icons:loading" />}
            disabled={
              finalFormDisabled || // ← conditionally disabled form
              (!formDataParsed?.disable_isDirty && !isDirty) // ← disable_isDirty override
            }
          >
            {formDataParsed.terms_acknowledgement
              ? t('continueButtonLabel', { defaultValue: 'Continue' })
              : typeof formDataParsed.submit_button_text === 'string'
                ? t(formDataParsed.submit_button_text, { defaultValue: formDataParsed.submit_button_text || 'Submit' })
                : isUpdateMode
                  ? t(formDataParsed.submit_button_text?.on_update || 'updateButtonLabel', { defaultValue: 'Update' })
                  : t(formDataParsed.submit_button_text?.default || 'createAccountButtonLabel', { defaultValue: 'Create Account' })}
          </LoadingButton>
        )}

        {/* Edit button (unchanged, still works) */}
        {formDataParsed?.edit_button_sql && (
          <LoadingButton
            sx={{ minWidth: 'max-content', width: 150, marginLeft: 2, marginTop: '10px' }}
            color="primary"
            size="large"
            variant="outlined"
            onClick={async () => {
              try {
                const payload: any = {
                  sql_data: formDataParsed?.edit_button_sql,
                  ...paramsData,
                  user_id: tokenData?.payload?.user?.UserId,
                  ComplianceReviewId: defaultData?.ComplianceReviewId,
                };

                if (isAdmin && module_name && sub_module) {
                  await executeAdminActions(module_name, sub_module, {
                    tk: EncryptUtils.encryptedData(payload),
                  });
                } else if (module_name) {
                  await executeUserActions(module_name, {
                    tk: EncryptUtils.encryptedData(payload),
                  });
                } else {
                  throw new Error('Module name is missing');
                }

                toast.success(t('formEditableSuccess', { defaultValue: 'Form is now editable.' }));
                refreshForm(); // re-fetch & re-check condition
              } catch (err: any) {
                toast.error(err?.message || t('formEditableFailed', { defaultValue: 'Failed to enable editing' }));
              }
            }}
          >
            {t(formDataParsed?.edit_button_text || 'editButtonLabel', { defaultValue: 'Edit' })}
          </LoadingButton>
        )}

        {defaultData && defaultData?.StageCode && formDataParsed?.rescind_button_sql && (
          <LoadingButton
            sx={{ minWidth: 'max-content', width: 150, marginLeft: 2, marginTop: '10px' }}
            color="error"
            size="large"
            variant="outlined"
            onClick={() => {
              const confirmation = formDataParsed?.confirmation;
              if (formDataParsed?.requires_confirmation && confirmation) {
                setConfirmationDialog({
                  open: true,
                  title: confirmation.title,
                  message: confirmation.message,
                  confirmText: confirmation.confirm_button_text,
                  cancelText: confirmation.cancel_button_text,
                  icon: confirmation.icon,
                  onConfirm: handleRescindApplication,
                });
              }
            }}
            disabled={defaultData?.Stage === 'Owner'}
          >
            {t(formDataParsed?.rescind_button_text || 'rescindButtonLabel', { defaultValue: 'Rescind' })}
          </LoadingButton>
        )}
      </Box>
    </Box>
  );

  return (
    <FormProvider {...methods}>
      <Form methods={methods} onSubmit={onSubmit}>
        <Box ref={scrollWrapperRef} sx={scrollNeeded ? { width: '100%', maxHeight: scrollMaxHeight } : { width: '100%' }}>
          {isFormDisabled && module_name === 'applications' && (
            <Typography fontStyle="italic" color="primary" variant="body2">
              {t('fullApplicationDisabledMessage', {
                defaultValue:
                  'The full application is disabled by default and is enabled by housing staff when a property is available and your position on the waitlist has been called upon.',
              })}
            </Typography>
          )}
          {formDataParsed?.helper_message && (
            <Typography fontStyle="italic" color="primary" variant="body2">
              {t(formDataParsed.helper_message, { defaultValue: formDataParsed.helper_message })}
            </Typography>
          )}
          {scrollNeeded ? (
            <Scrollbar sx={{ width: '100%', maxHeight: scrollMaxHeight }}>{renderFields}</Scrollbar>
          ) : (
            <Box sx={{ width: '100%' }}>{renderFields}</Box>
          )}
        </Box>

        {/* Terms and Conditions dialog */}
        {dialog?.value && (
          <Dialog
            open={dialog.value}
            onClose={() => {
              setAcknowledged(false);
              dialog.onFalse();
            }}
            scroll="paper"
          >
            <DialogTitle sx={{ pb: 2 }}>
              {t(formDataParsed?.terms_acknowledgement_heading || 'termsAcknowledgementHeading', { defaultValue: 'Terms and Conditions' })}
            </DialogTitle>
            <DialogContent dividers>
              <Markdown>{formattedMarkdown}</Markdown>
              <Typography sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Checkbox
                  checked={acknowledged}
                  sx={{ marginBottom: 0 }}
                  onChange={() => {
                    setAcknowledged(!acknowledged);
                    const ssItem: string = (!acknowledged).toString();
                    sessionStorage.setItem('eligibilityAcknowledge', ssItem);
                  }}
                />
                {isEs && formDataParsed?.terms_acknowledgement_text_es
                  ? formDataParsed.terms_acknowledgement_text_es
                  : t('iAgreeLabel', { defaultValue: formDataParsed?.terms_acknowledgement_text || 'I Agree' })}
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => {
                  setAcknowledged(false);
                  dialog.onFalse();
                }}
              >
                {t('cancelLabel', { defaultValue: 'Cancel' })}
              </Button>
              <Button
                variant="contained"
                disabled={!acknowledged}
                onClick={() => {
                  let hasError = false;
                  if (emailWatchedValue && emailChanged && isEmailValid && !otpVerified) {
                    setError('Email', {
                      type: 'manual',
                      message: 'Please verify your email using the OTP before proceeding.',
                    });
                    toast.error(t('verifyEmailOtpFirst', { defaultValue: 'Please verify your email with OTP before proceeding.' }));
                    hasError = true;
                  }
                  if (hasError) return;
                  dialog.onFalse();
                  onSubmit();
                }}
              >
                {t('submitLabel', { defaultValue: formDataParsed?.submit_button_text || 'Submit' })}
              </Button>
            </DialogActions>
          </Dialog>
        )}

        {/* Success dialog */}
        {dialogOpen && (
          <Dialog open={dialogOpen} onClose={handleDialogClose}>
            <DialogTitle>{t('successLabel', { defaultValue: 'Success' })}</DialogTitle>
            <DialogContent>
              <Typography>{dialogMessage}</Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleDialogClose}>{t('closeLabel', { defaultValue: 'Close' })}</Button>
            </DialogActions>
          </Dialog>
        )}

        {/* Dynamic Dialog */}
        {confirmationDialog?.open && (
          <Dialog
            open={confirmationDialog.open}
            onClose={() => setConfirmationDialog({ ...confirmationDialog, open: false })}
          >
            <DialogTitle>{confirmationDialog.title || t('confirmationTitle', { defaultValue: 'Confirmation' })}</DialogTitle>
            <DialogContent>
              <Box display="flex" alignItems="center" flexDirection="column" textAlign="center">
                {confirmationDialog.icon && (
                  <Iconify icon={confirmationDialog.icon} color="red" width={48} height={48} />
                )}
                <Typography mt={2}>{confirmationDialog.message}</Typography>
              </Box>
            </DialogContent>

            <DialogActions>
              <Button onClick={() => setConfirmationDialog((prev) => ({ ...prev, open: false }))}>
                {confirmationDialog.cancelText || t('cancelLabel', { defaultValue: 'Cancel' })}
              </Button>
              <Button
                color="error"
                variant="contained"
                onClick={async () => {
                  await confirmationDialog.onConfirm?.();
                  setConfirmationDialog((prev) => ({ ...prev, open: false }));
                }}
              >
                {confirmationDialog.confirmText || t('confirmLabel', { defaultValue: 'Confirm' })}
              </Button>
            </DialogActions>
          </Dialog>
        )}

        {/* Revert to Defaults Dialog for Control Panel */}
        {module_name === 'control-panel' && sub_module && (
          <Dialog
            open={revertDialogOpen}
            onClose={() => !reverting && setRevertDialogOpen(false)}
            aria-labelledby="revert-dialog-title"
            aria-describedby="revert-dialog-description"
          >
            <DialogTitle id="revert-dialog-title">
              {t('revertDialogTitleWithModule', { defaultValue: 'Revert {{module}} to Defaults?', module: sub_module.replace(/-/g, ' ') })}
            </DialogTitle>
            <DialogContent>
              <DialogContentText id="revert-dialog-description">
                {t('revertDialogDescription', { defaultValue: 'This action will reset all configurations for this tab to their default values from the JSON file. This action cannot be undone.' })}
                <br />
                <br />
                {t('revertDialogConfirmQuestion', { defaultValue: 'Are you sure you want to continue?' })}
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setRevertDialogOpen(false)} disabled={reverting}>
                {t('cancelLabel', { defaultValue: 'Cancel' })}
              </Button>
              <LoadingButton
                onClick={async () => {
                  // Permission check: Revert button is only shown if user has permission (checked at render)
                  // This is a safety check in case permissions change or edge cases
                  if (!canEditControlPanel) {
                    setRevertDialogOpen(false);
                    return;
                  }
                  
                  try {
                    setReverting(true);
                    await revertTabToDefaults(sub_module);
                    toast.success(`${sub_module.replace(/-/g, ' ')} ${t('revertSuccessMessage', { defaultValue: 'reverted to default values successfully' })}`);
                    setRevertDialogOpen(false);
                    // Refresh the form to show default values
                    if (refreshForm) {
                      refreshForm();
                    }
                  } catch (error: any) {
                    toast.error(error?.message || t('revertFailedMessage', { defaultValue: 'Failed to revert to defaults' }));
                  } finally {
                    setReverting(false);
                  }
                }}
                color="error"
                variant="contained"
                disabled={reverting}
                loading={reverting}
                startIcon={reverting ? <Iconify icon="svg-spinners:3-dots-fade" /> : <Iconify icon="solar:restart-bold" />}
              >
                {reverting ? t('revertingLabel', { defaultValue: 'Reverting...' }) : t('revertToDefaultsLabel', { defaultValue: 'Revert to Defaults' })}
              </LoadingButton>
            </DialogActions>
          </Dialog>
        )}
      </Form>
    </FormProvider>
  );
}
